import useApi from '~/hooks/useApi';
import { runDownloadTask } from '~/utils/runDownloadTask';
import { cpfUnmask } from '~/utils/masks';
import {
  IDialogInfo,
  IResponseCustomerDataPaginated,
  IResponseCustomerData,
  IResponseDownloadCSV,
  ICustomer,
  IResponseDetailsCampaing,
} from '../models/interfaceModels';
import moment from 'moment';
import { IFormCampaingData } from '../components/campaingData';

/**
 * Remove os clientes selecionados da campanha, utilizando o CPF para identificar quais clientes devem ser removidos.
 * Usa como fonte de informação os clientes selecionados na tabela pelo checkbox.
 * Após a remoção, a lista de clientes atingidos pela campanha é atualizada para refletir as mudanças.
 *
 * @param selectedCustomers Lista de clientes selecionados para remoção, onde cada cliente é representado por um array de valores (incluindo o CPF).
 * @param POSTRequestUseApi Função para realizar a requisição POST para remoção dos clientes.
 * @param setInfoDialog Função para atualizar o estado do diálogo de informações, exibindo mensagens de sucesso ou erro.
 * @returns Retorna a resposta da API em caso de sucesso ou exibe uma mensagem de erro em caso de falha.
 */
export const handleRemoveSelected = async (
  selectedCustomers: any[],
  POSTRequestUseApi: ReturnType<typeof useApi>,
  setInfoDialog: React.Dispatch<React.SetStateAction<IDialogInfo>>,
) => {
  const cpfsToRemove = selectedCustomers.map((r: any) => cpfUnmask(r[3].value));
  console.log('Selected CPFs:', cpfsToRemove);
  const response = await POSTRequestUseApi.callApi({
    clientesCpf: cpfsToRemove,
  });

  if (response.status > 300) {
    setInfoDialog({
      open: true,
      message:
        response.mensagem ||
        'Erro ao remover cliente da campanha. Tente novamente!',
      error: true,
      isGoBack: false,
    });
  } else {
    return response;
  }
};

/**
 * Busca cliente por CPF e nome para inserção manual.
 *
 * @param idCampaing ID da campanha para a qual os clientes serão inseridos manualmente.
 * @param GETRequestUseApi Função para realizar a requisição GET para busca dos clientes.
 * @param setInfoDialog Função para atualizar o estado do diálogo de informações, exibindo mensagens de sucesso ou erro.
 * @param setTotalPagesCustomerManual Função para atualizar o total de páginas disponíveis para a busca manual.
 * @param setTotalItemsCustomerManual Função para atualizar o total de itens encontrados na busca manual.
 * @returns Retorna a lista de clientes encontrados com base nos critérios de busca ou exibe uma mensagem de erro em caso de falha.
 */
export const searchClientListManual = async (
  idCampaing: string,
  GETRequestUseApi: ReturnType<typeof useApi>,
  setInfoDialog: React.Dispatch<React.SetStateAction<IDialogInfo>>,
  setTotalPagesCustomerManual: React.Dispatch<React.SetStateAction<number>>,
  setTotalItemsCustomerManual: React.Dispatch<
    React.SetStateAction<number | null>
  >,
) => {
  const hasIdCampaing = idCampaing.trim() !== '';

  if (!hasIdCampaing) {
    setInfoDialog({
      open: true,
      message:
        'Crie a campanha manual antes de consultar clientes para inserção.',
      error: true,
      isGoBack: false,
    });
    return;
  }

  const response = await GETRequestUseApi.callApi();

  const {
    totalPages,
    totalSize,
    data,
  } = response.data as IResponseCustomerDataPaginated;

  if (response.status >= 200 && response.status < 300) {
    setTotalPagesCustomerManual(totalPages);
    setTotalItemsCustomerManual(totalSize);

    return data;
  } else {
    setTotalPagesCustomerManual(0);
    setTotalItemsCustomerManual(0);
    setInfoDialog({
      open: true,
      message:
        response.mensagem ||
        'Erro ao buscar a lista de clientes para inclusão manual. Tente novamente!',
      error: true,
      isGoBack: false,
    });
  }
};

/**
 * Inclui cliente manualmente na campanha.
 *
 * @param customer Objeto representando o cliente a ser incluído, contendo informações como CPF, nome completo, celular, número do cartão, email, bairro, UF, cidade, status do cartão, nível e limite disponível.
 * @param POSTRequestUseApi Função para realizar a requisição POST para inclusão do cliente.
 * @param handleListUpdateRemove Função para atualizar a lista de clientes atingidos pela campanha após a inclusão manual.
 * @param setInfoDialog Função para atualizar o estado do diálogo de informações, exibindo mensagens de sucesso ou erro.
 * @returns Retorna a resposta da API em caso de sucesso ou exibe uma mensagem de erro em caso de falha.
 */
export const onClickIncludeCustomerManually = async (
  customer: IResponseCustomerData,
  POSTRequestUseApi: ReturnType<typeof useApi>,
  handleListUpdateRemove: (customer: IResponseCustomerData) => void,
  setInfoDialog: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      message: string;
      error: boolean;
      isGoBack: boolean;
    }>
  >,
) => {
  const response = await POSTRequestUseApi.callApi({
    cpf: customer.cpf,
    nomeCompleto: customer.nomeCompleto,
    celular: customer.celular,
    numeroCartao: customer.numeroCartao,
    email: customer.email,
    bairro: customer.bairro,
    uf: customer.uf,
    cidade: customer.cidade,
    statusCartao: customer.statusCartao,
    nivel: customer.nivel,
    limiteDisponivel: customer.limiteDisponivel,
  });

  if (response.status > 300) {
    if (response.status === 409) {
      handleListUpdateRemove(customer);
    }

    setInfoDialog({
      open: true,
      message:
        response.mensagem ||
        'Erro ao incluir cliente na campanha. Tente novamente!',
      error: true,
      isGoBack: false,
    });
  } else {
    return response;
  }
};

/**
 * Busca a lista de clientes atingidos pela campanha.
 * @param GETRequestUseApi Função para realizar a requisição GET.
 * @param setInfoDialog Função para atualizar o estado do diálogo de informações.
 * @param setTotalPages Função para atualizar o total de páginas.
 * @param setTotalItems Função para atualizar o total de itens.
 * @returns Status e dados da campanha.
 */
export const searchClientList = async (
  GETRequestUseApi: ReturnType<typeof useApi>,
  setInfoDialog: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      message: string;
      error: boolean;
      isGoBack: boolean;
    }>
  >,
  setTotalPages: React.Dispatch<React.SetStateAction<number>>,
  setTotalItems: React.Dispatch<React.SetStateAction<number | null>>,
) => {
  const response = await GETRequestUseApi.callApi();

  const { totalDePaginas, totalItens } = response.data as ICustomer;

  if (response.status >= 300) {
    setTotalPages(0);
    setTotalItems(0);
    setInfoDialog({
      open: true,
      message:
        response.mensagem ||
        'Erro ao buscar a lista de clientes atingidos pela campanha. Tente novamente!',
      error: true,
      isGoBack: true,
    });
  } else {
    setTotalPages(totalDePaginas);
    setTotalItems(totalItens);
    return { status: response.status, data: response.data as ICustomer };
  }
};

/**
 * Realiza o download do arquivo CSV com a lista completa de clientes atingidos pela campanha.
 * @param GETRequestUseApi Função para realizar a requisição GET.
 * @param setDownloadCVSProgress Função para atualizar o progresso do download.
 * @param setInfoDialog Função para atualizar o estado do diálogo de informações.
 */
export const onClickDownloadCSV = async (
  GETRequestUseApi: ReturnType<typeof useApi>,
  setDownloadCVSProgress: React.Dispatch<React.SetStateAction<string>>,
  setInfoDialog: React.Dispatch<React.SetStateAction<IDialogInfo>>,
) =>
  runDownloadTask(async () => {
    try {
      setDownloadCVSProgress('Preparando download do arquivo CSV...');
    const response = await GETRequestUseApi.callApi();

    const data = response.data as IResponseDownloadCSV;

    if (response.status >= 200 && response.status < 300) {
      const binaryString = atob(data.base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename;
      link.click();

      setDownloadCVSProgress('');
      setInfoDialog({
        open: true,
        message: 'Download do arquivo CSV realizado com sucesso!',
        error: false,
        isGoBack: false,
      });
    } else {
      setInfoDialog({
        open: true,
        message:
          response.mensagem ||
          'Algo deu errado ao preparar o download do arquivo CSV. Tente novamente.',
        error: true,
        isGoBack: false,
      });
    }
  } catch (error) {
    setInfoDialog({
      open: true,
      message:
        'Algo deu errado ao preparar o download do arquivo CSV. Tente novamente.',
      error: true,
      isGoBack: false,
    });
    }
  });

/**
 * Cancela a campanha.
 *
 * @param DELETERequestUseApi Função para realizar a requisição DELETE para cancelar a campanha.
 * @param setInfoDialog Função para atualizar o estado do diálogo de informações, exibindo mensagens de sucesso ou erro.
 * @returns Retorna a resposta da API em caso de sucesso ou exibe uma mensagem de erro em caso de falha.
 */
export const onClickCancelCampaing = async (
  DELETERequestUseApi: ReturnType<typeof useApi>,
  setInfoDialog: React.Dispatch<React.SetStateAction<IDialogInfo>>,
) => {
  const response = await DELETERequestUseApi.callApi();

  if (response.status >= 200 && response.status < 300) {
    setInfoDialog({
      open: true,
      message: 'Campanha cancelada com sucesso!',
      error: false,
      isGoBack: false,
    });
  } else {
    setInfoDialog({
      open: true,
      message:
        response.mensagem || 'Erro ao cancelar campanha. Tente novamente!',
      error: true,
      isGoBack: false,
    });
  }
};

/**
 * Busca os detalhes da campanha.
 *
 * @param GETRequestUseApi Função para realizar a requisição GET para buscar os detalhes da campanha.
 * @param setForm Função para atualizar o estado do formulário com os detalhes da campanha.
 * @param setDataInfoCampaing Função para atualizar o estado com as informações da campanha.
 * @param setInfoDialog Função para atualizar o estado do diálogo de informações, exibindo mensagens de sucesso ou erro.
 * @returns Retorna os detalhes da campanha em caso de sucesso ou exibe uma mensagem de erro em caso de falha.
 */
export const getDetailsCampaing = async (
  GETRequestUseApi: ReturnType<typeof useApi>,
  setForm: React.Dispatch<React.SetStateAction<IFormCampaingData>>,
  setInfoDialog: React.Dispatch<React.SetStateAction<IDialogInfo>>,
  setDataInfoCampaing?: React.Dispatch<
    React.SetStateAction<IResponseDetailsCampaing | null>
  >,
) => {
  const response = await GETRequestUseApi.callApi();

  const data = response.data as IResponseDetailsCampaing;
  if (response.status >= 200 && response.status < 300) {
    const auxDateValidity = moment(data.dataValidade).format('DD/MM/YYYY');
    const auxHourValidity = moment(data.dataValidade).format('HH:mm:ss');
    const auxDateStart = moment(data.dataAgendamento).format('DD/MM/YYYY');
    const auxHourStart = moment(data.dataAgendamento).format('HH:mm:ss');
    setForm({
      descCampanha: data.nome,
      dtaValidade: auxDateValidity,
      dtaInicio: auxDateStart,
      horaInicio: auxHourStart,
      horaValidade: auxHourValidity,
      switchDtaExecucao: data.dataAgendamento ? true : false,
    });
    if (setDataInfoCampaing) {
      setDataInfoCampaing(data);
    }
  } else
    setInfoDialog({
      open: true,
      message:
        response.mensagem ||
        'Erro ao buscar os detalhes da campanha. Tente novamente!',
      error: true,
      isGoBack: false,
    });
};

/**
 * Atualiza os dados básicos da campanha, como nome e data de validade/agendamento.
 * 
 * @param POSTRequestUseApi Função para realizar a requisição POST para atualizar os dados da campanha.
 * @param setInfoDialog Função para atualizar o estado do diálogo de informações, exibindo mensagens de sucesso ou erro.
 * @param form Objeto contendo os dados do formulário para atualização da campanha, incluindo descrição, data de validade, data de início, hora de início, hora de validade e um booleano indicando se a data de execução é agendada.
 * @returns Retorna a resposta da API em caso de sucesso ou exibe uma mensagem de erro em caso de falha.
 */
export const handleUpdateCampaingBasicData = async (
  POSTRequestUseApi: ReturnType<typeof useApi>,
  setInfoDialog: React.Dispatch<React.SetStateAction<IDialogInfo>>,
  form: IFormCampaingData
) => {
  const payload = {
    nome: form.descCampanha,
    dataValidade: !form.switchDtaExecucao
      ? `${form.dtaValidade.split('/')[2]}-${form.dtaValidade.split('/')[1]}-${
          form.dtaValidade.split('/')[0]
        }T${form.horaValidade}:00`
      : null,
    dataAgendamento: form.switchDtaExecucao
      ? `${form.dtaInicio.split('/')[2]}-${form.dtaInicio.split('/')[1]}-${
          form.dtaInicio.split('/')[0]
        }T${form.horaInicio}:00`
      : null,
  };

  const response = await POSTRequestUseApi.callApi(payload);

  if (response.status >= 300) {
    setInfoDialog({
      open: true,
      message:
        response.mensagem ||
        'Erro ao atualizar os dados da campanha. Tente novamente!',
      error: true,
      isGoBack: false,
    });
  } else {
    return response;
  }
};
